import { zodResolver } from "@hookform/resolvers/zod";
import { ChevronLeft } from "lucide-react";
import React, { useMemo, useState } from "react";
import {
  FormProvider,
  useForm,
  type FieldErrors,
} from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import { Load } from "components/load";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { toast } from "components/ui/use-toast";
import { PageLayout } from "components/layout";

import {
  CampaignType,
  GamificationStatus,
  VoucherValidityMode,
} from "../../../../@types/gamification";
import {
  useLazyValidateGamificationPeriodQuery,
  useUpdateGamificationMutation,
} from "../../../../services/gamification";
import {
  useGetStoresOptionsWithDetailsQuery,
  type IStoreOptionWithDetails,
} from "../../../../services/store/select-options";
import { STORE_OPTION_VALUES } from "../constants/store-options";
import { DEFAULT_CAMPAIGN_PRIORITY } from "../constants/priority-levels";
import { getDefaultPrizes } from "../constants/mechanic-defaults";
import {
  getStatusColor,
  getStatusDescription,
  isCampaignActive,
} from "../utils/status";
import {
  updateGamificationSchema,
  type UpdateGamificationFormData,
} from "../validations/gamification.validation";
import { BannersCard } from "./components/BannersCard";
import { BasicInfoCard } from "./components/BasicInfoCard";
import { RegulationsCard } from "./components/RegulationsCard";
import { StoresCard } from "./components/StoresCard";
import { MechanicConfigSection } from "../components/mechanic-config-section";
import { PrizeTable } from "../components/prize-table";
import { SegmentationCard } from "./components/SegmentationCard";
import { firstErrorMessage, SUBMIT_BLOCKED_FALLBACK } from "../wizard/form-errors";
import { GamificationWizard } from "../wizard/GamificationWizard";
import { UnsavedChangesDialog } from "../wizard/UnsavedChangesDialog";
import {
  LEAVE_WITHOUT_GUARD,
  useUnsavedChangesGuard,
} from "../wizard/useUnsavedChangesGuard";
import { ReviewStep } from "../create/components/ReviewStep";
import { StepFaq } from "../create/components/steps/StepFaq";
import {
  getPendingIssues,
  getStepBlockReason,
  REVIEW_STEP,
  type StepGuardOptions,
  WIZARD_STEPS,
  WizardStep,
} from "../wizard/steps";
import { CampaignLinkBlock } from "../components/campaign-link-block";
import { useCampaignSlugSuggestion } from "../hooks/use-campaign-slug-suggestion";
import {
  type SlugAvailability,
  useSlugAvailability,
} from "../hooks/use-slug-availability";
import { useBannerState } from "../hooks/use-banner-state";
import { useCampaignData } from "./hooks/useCampaignData";
import { useEditStoreManager } from "./hooks/useEditStoreManager";
import { useFormInitialization } from "./hooks/useFormInitialization";
import { useFormSubmit } from "./hooks/useFormSubmit";
import { usePendingSegmentation } from "./hooks/usePendingSegmentation";
import { SEGMENTATION_COPY } from "../constants/messages";

export const EditGamification: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const campaignId = parseInt(id || "0", 10);

  const [updateCampaign, { isLoading: isUpdating }] =
    useUpdateGamificationMutation();
  const [validatePeriod] = useLazyValidateGamificationPeriodQuery();
  const [currentStep, setCurrentStep] = useState<number>(WizardStep.BASICS);
  const { campaign, isLoading, error } = useCampaignData(campaignId);
  const {
    data: storeOptions = [],
    isLoading: isLoadingStores,
    isFetching: isFetchingStores,
  } = useGetStoresOptionsWithDetailsQuery();

  const form = useForm<UpdateGamificationFormData>({
    resolver: zodResolver(updateGamificationSchema),
    defaultValues: {
      id: campaignId,
      campaignName: "",
      slug: "",
      campaignType: CampaignType.ROULETTE,
      startDate: "",
      endDate: "",
      stores: [],
      mechanicConfig: {
        prizes: getDefaultPrizes(CampaignType.ROULETTE),
        maxPlays: undefined,
        minPurchase: undefined,
        priority: DEFAULT_CAMPAIGN_PRIORITY,
        concurrency: { competes: false, group: "" },
        voucherValidity: {
          mode: VoucherValidityMode.DAYS,
          validityDays: 30,
          startOffsetDays: 0,
        },
        cpfSegmentation: { enabled: false },
        mechanicParams: { goalProbability: undefined, symbols: [] },
        presentation: { customerFacingName: "", customerFacingDescription: "" },
      },
      faqItems: [],
      regulations: undefined,
      allStoresSelected: false,
    },
  });

  const existingStoreCodes = useMemo(() => {
    if (!campaign?.stores) return new Set<string>();
    return new Set(campaign.stores.map((store) => String(store.storeCode)));
  }, [campaign]);

  const storeOptionsMap = useMemo(
    () =>
      new Map(
        storeOptions.map((store: IStoreOptionWithDetails) => [store.value, store]),
      ),
    [storeOptions],
  );

  const isDraftCampaign = campaign?.status === GamificationStatus.RASCUNHO;

  const {
    storeFields,
    storeCodesInput,
    setStoreCodesInput,
    handleAddStore,
    handleAddStoresByCodes,
    handleRemoveStore,
  } = useEditStoreManager({
    form,
    storeOptionsMap,
    existingStoreCodes,
    lockExistingStores: !isDraftCampaign,
  });

  const campaignType = form.watch("campaignType") ?? CampaignType.ROULETTE;
  const watchCampaignName = form.watch("campaignName");
  const watchStores = form.watch("stores");
  const allStoresSelected = form.watch("allStoresSelected");

  const selectedStoreCodes = (watchStores || [])
    .map((store) => String(store.storeCode))
    .filter(Boolean);

  const comboboxOptions = useMemo(
    () =>
      storeOptions.map((store: IStoreOptionWithDetails) => ({
        value: store.value,
        label: "Loja " + store.value,
      })),
    [storeOptions],
  );

  const filteredComboboxOptions = useMemo(
    () =>
      comboboxOptions.filter(
        (store) => !selectedStoreCodes.includes(String(store.value)),
      ),
    [comboboxOptions, selectedStoreCodes],
  );

  const storeOptionsWithAll = allStoresSelected
    ? comboboxOptions
    : [
        { value: STORE_OPTION_VALUES.ALL_STORES, label: "Todas as Lojas" },
        ...filteredComboboxOptions,
      ];

  const isCampaignActiveStatus = campaign
    ? isCampaignActive(campaign.status)
    : false;

  const banners = useBannerState(form, {
    banner3x4Url: campaign?.banner3x4Url,
    banner16x9Url: campaign?.banner16x9Url,
  });

  const slugAvailability: SlugAvailability = useSlugAvailability(
    form.control,
    campaignId,
  );
  useCampaignSlugSuggestion(form, slugAvailability.status);

  const { onSubmit } = useFormSubmit({
    campaignId,
    updateCampaign,
    validatePeriod,
    onSlugConflict: (message: string): void => {
      form.setError("slug", { type: "manual", message });
      setCurrentStep(WizardStep.CONFIG);
    },
  });

  useFormInitialization({ campaign, reset: form.reset });

  const handleInvalidSubmit = (
    errors: FieldErrors<UpdateGamificationFormData>,
  ) => {
    const reason = firstErrorMessage(errors) ?? SUBMIT_BLOCKED_FALLBACK;
    toast({
      description: `Não foi possível salvar: ${reason}`,
      variant: "destructive",
    });
  };

  const guardOptions: StepGuardOptions = {
    requireCoverImage: !campaign?.hasBanner3x4,
    requireWideBanner: !campaign?.hasBanner16x9,
    requireSegmentationFile: false,
    slugStatus: slugAvailability.status,
  };

  const watchedValues = form.watch();
  const pendingIssues = getPendingIssues(watchedValues, guardOptions);

  const publishedBlockReason: string | null =
    getStepBlockReason(WizardStep.CONFIG, watchedValues, guardOptions) ??
    getStepBlockReason(WizardStep.FILES, watchedValues, guardOptions);

  const hasPendingSegmentation: boolean = usePendingSegmentation(form);

  const leaveGuard = useUnsavedChangesGuard(form.formState.isDirty, () =>
    navigate("/crm/gestao-gameficacao", { state: LEAVE_WITHOUT_GUARD }),
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Load />
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate("/crm/gestao-gameficacao")}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-bold">Erro ao carregar campanha</h1>
        </div>
        <p className="text-red-500">
          Campanha não encontrada ou erro ao carregar dados.
        </p>
      </div>
    );
  }

  const renderStep = (step: number): React.ReactNode => {
    const byStep: Record<number, React.ReactNode> = {
      [WizardStep.BASICS]: (
        <BasicInfoCard
          register={form.register}
          control={form.control}
          errors={form.formState.errors}
          campaignNameLength={watchCampaignName?.length || 0}
        />
      ),
      [WizardStep.STORES]: (
        <StoresCard
          register={form.register}
          storeFields={storeFields}
          storeOptionsWithAll={storeOptionsWithAll}
          allStoresSelected={allStoresSelected || false}
          isLoadingStores={isLoadingStores || isFetchingStores}
          errors={form.formState.errors}
          onAddStore={handleAddStore}
          onRemoveStore={handleRemoveStore}
          storeCodesInput={storeCodesInput}
          setStoreCodesInput={setStoreCodesInput}
          onAddStoresByCodes={handleAddStoresByCodes}
          comboboxOptions={comboboxOptions}
          existingStoreCodes={existingStoreCodes}
          isCampaignActive={isCampaignActiveStatus}
          lockExistingStores={!isDraftCampaign}
        />
      ),
      [WizardStep.PRIZES]: (
        <PrizeTable
          campaignType={campaignType}
          control={form.control}
          register={form.register}
          errors={form.formState.errors}
        />
      ),
      [WizardStep.CONFIG]: (
        <MechanicConfigSection
          campaignType={campaignType}
          control={form.control}
          register={form.register}
          errors={form.formState.errors}
          currentCampaignId={campaignId}
          currentCreatedAt={campaign.createdAt}
          generalLead={<CampaignLinkBlock availability={slugAvailability} />}
        />
      ),
      [WizardStep.SEGMENTATION]: (
        <SegmentationCard campaignId={campaign.idCapa ?? campaignId} />
      ),
      [WizardStep.FILES]: (
        <>
          <BannersCard
            errors={form.formState.errors}
            cover={banners.cover}
            alternative={banners.alternative}
            hasBanner3x4={campaign.hasBanner3x4 || false}
            hasBanner16x9={campaign.hasBanner16x9 || false}
            campaignType={campaignType}
          />
          <RegulationsCard
            setValue={form.setValue}
            errors={form.formState.errors}
            hasRegulationFile={campaign.hasRegulationFile || false}
            regulationsUrl={campaign.regulations}
          />
        </>
      ),
      [WizardStep.FAQ]: <StepFaq />,
      [WizardStep.REVIEW]: (
        <ReviewStep
          values={form.getValues()}
          pendingIssues={pendingIssues}
          banner3x4Preview={banners.cover.preview}
          banner16x9Preview={banners.alternative.preview}
          hasExistingBanner3x4={campaign.hasBanner3x4 || false}
          hasExistingBanner16x9={campaign.hasBanner16x9 || false}
          hasExistingRegulation={campaign.hasRegulationFile || false}
        />
      ),
    };
    return byStep[step] ?? null;
  };

  const campaignBlockReason: string = isDraftCampaign
    ? pendingIssues.join(" · ")
    : publishedBlockReason ?? "";
  const submitBlockReason: string = hasPendingSegmentation
    ? SEGMENTATION_COPY.pendingBase
    : campaignBlockReason;

  const resolveSubmitLabel = (): string => {
    if (isUpdating) return "Salvando...";
    if (isDraftCampaign) return "Publicar";
    return "Salvar Alterações";
  };
  const submitLabel = resolveSubmitLabel();

  return (
    <PageLayout>
      <FormProvider {...form}>
        <GamificationWizard<UpdateGamificationFormData>
          title="Editar Campanha de Gameficação"
          headerBadge={
            <Badge className={getStatusColor(campaign.status)}>
              {getStatusDescription(campaign.status)}
            </Badge>
          }
          steps={WIZARD_STEPS}
          currentStep={currentStep}
          onStepChange={setCurrentStep}
          reviewStep={REVIEW_STEP}
          guardOptions={guardOptions}
          renderStep={renderStep}
          allowForwardNavigation
          onCancel={leaveGuard.requestLeave}
          submit={{
            label: submitLabel,
            disabled: isUpdating || submitBlockReason !== "",
            title: submitBlockReason,
            onValid: (data) => onSubmit(data, { publish: isDraftCampaign }),
            onInvalid: handleInvalidSubmit,
          }}
        />
        <UnsavedChangesDialog
          open={leaveGuard.confirmOpen}
          onConfirm={leaveGuard.confirmLeave}
          onCancel={leaveGuard.cancelLeave}
        />
      </FormProvider>
    </PageLayout>
  );
};
